<template>
  <b-overlay :show="loading" variant="white" :opacity="1">
    <b-card class="calendar-card">
      <full-calendar ref="fullCalendar" :options="calendarOptions" />
    </b-card>
  </b-overlay>
</template>

<script>
import "@fullcalendar/common/main.css";
import "@fullcalendar/daygrid/main.css";
import { BOverlay, BCard } from "bootstrap-vue";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";


export default {
  components: {
    BOverlay,
    BCard,
    FullCalendar,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    active(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.fullCalendar.getApi().updateSize();
        });
      }
    },
  },
  computed: {
    loading() {
      return this.$store.getters["accommodation/loadingBookings"];
    },
    bookings() {
      return this.$store.getters["accommodation/bookings"];
    },
    calendarEvents() {
      const events = [];

      this.bookings.forEach((booking) => {
        const checkin = booking.checkin?.split("T")[0];
        const checkout = booking.checkout?.split("T")[0];
        if (!checkin || !checkout) return;

        const isSameDay = checkin === checkout;
        const color = "#ffcd00";
        const checkinNext = this.$moment(checkin).add(1, "day").format("YYYY-MM-DD");
        const checkoutNext = this.$moment(checkout).add(1, "day").format("YYYY-MM-DD");

        if (isSameDay) {
          // Same-day: orange background, no main event bar
          events.push({
            start: checkin,
            end: checkinNext,
            display: "background",
            backgroundColor: "rgba(255, 159, 67, 0.4)",
          });
        } else {
          // Main booking event
          const adults = booking.adults || 0;
          const children = booking.children || 0;
          const babies = booking.babies || 0;
          const checkinFormatted = this.$moment(checkin).format("DD/MM");
          const checkoutFormatted = this.$moment(checkout).format("DD/MM");
          events.push({
            id: booking.localizator,
            title: `\uD83D\uDCD3 ${booking.localizator} - \uD83D\uDCC5 ${checkinFormatted} -> ${checkoutFormatted} - \uD83D\uDC64 ${adults}-${children}-${babies}`,
            start: checkin,
            end: checkoutNext,
            backgroundColor: color,
            borderColor: color,
            extendedProps: { booking },
          });

          // Checkin day: light green background
          events.push({
            start: checkin,
            end: checkinNext,
            display: "background",
            backgroundColor: "rgba(40, 199, 111, 0.35)",
          });

          // Checkout day: light red background
          events.push({
            start: checkout,
            end: checkoutNext,
            display: "background",
            backgroundColor: "rgba(234, 84, 85, 0.35)",
          });
        }
      });

      return events;
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        headerToolbar: {
          left: "prev,next",
          center: "title",
          right: "today",
        },
        events: this.calendarEvents,
        locale: this.$i18n?.locale || "en",
        buttonText: { today: this.$t("Today") },
        firstDay: 1,
        eventDisplay: "block",
        displayEventTime: false,
        height: "auto",
      };
    },
  },
};
</script>

<style lang="scss">
$primary: #ffcd00;

.calendar-card {
  .fc-toolbar-title {
    font-size: 1.1em;
  }

  .fc-event {
    cursor: default;
    padding: 2px 4px;
    font-size: 0.8rem;
  }

  .fc-button-primary {
    background-color: $primary;
    border-color: $primary;
    color: #1a1a1a;

    &:hover,
    &:focus,
    &:active,
    &:not(:disabled):active {
      background-color: darken($primary, 10%);
      border-color: darken($primary, 10%);
      color: #1a1a1a;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba($primary, 0.5);
    }

    &:disabled {
      background-color: $primary;
      border-color: $primary;
      color: #1a1a1a;
      opacity: 0.65;
    }
  }
}
</style>
